import { Map, FileText, Network, Send, Type } from 'lucide-react';
import { type KeyboardEvent, useLayoutEffect, useRef, useState } from 'react';

import { Button, Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui';
import { cn } from '@/lib/cn';
import type { ResponseFormat } from '@/types/api';

const MAX_CHARS = 2000;
const COUNTER_THRESHOLD = 1800;

interface ChatInputBarProps {
  isSubmitting: boolean;
  quotaExhausted: boolean;
  responseFormat: ResponseFormat;
  onResponseFormatChange: (format: ResponseFormat) => void;
  onSubmit: (question: string) => void;
  disabledFormats?: ResponseFormat[];
  /** Seeds the input on mount — used by deep links such as the map popup. */
  initialValue?: string;
}

/** Auto-expanding input bar. Spec S04 §4 — 1→6 lines, 2000 chars, Shift+Enter newline. */
export function ChatInputBar({
  isSubmitting,
  quotaExhausted,
  responseFormat,
  onResponseFormatChange,
  onSubmit,
  disabledFormats = [],
  initialValue,
}: ChatInputBarProps) {
  // `useState`'s lazy initializer only fires on the first render, so a
  // later parent re-render with the same `initialValue` won't clobber
  // analyst edits.
  const [value, setValue] = useState(() => initialValue ?? '');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Resize textarea between min 1-line and max 6-line heights.
  useLayoutEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = '0px';
    el.style.height = `${Math.min(el.scrollHeight, 180)}px`;
  }, [value]);

  const submit = () => {
    const trimmed = value.trim();
    if (!trimmed || isSubmitting || quotaExhausted) return;
    onSubmit(trimmed);
    setValue('');
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  };

  const charCount = value.length;

  return (
    <div className="border-t border-slate-200 bg-white px-4 py-3">
      {quotaExhausted && (
        <div
          className="mb-2 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-small text-amber-800"
          role="status"
        >
          Daily query limit reached — try again after midnight UTC.
        </div>
      )}
      <div className="flex items-end gap-2">
        <div className="flex-1 rounded-lg border border-slate-300 bg-white px-3 py-2 focus-within:border-accent focus-within:ring-2 focus-within:ring-accent">
          <textarea
            ref={textareaRef}
            value={value}
            disabled={quotaExhausted}
            onChange={(e) => {
              if (e.target.value.length <= MAX_CHARS) setValue(e.target.value);
            }}
            onKeyDown={handleKeyDown}
            placeholder="Ask about the energy transition…"
            rows={1}
            className="block w-full resize-none border-0 bg-transparent text-body text-slate-900 placeholder:text-slate-400 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
            aria-label="Question input"
          />
          <div className="mt-1 flex items-center justify-between gap-2">
            <FormatPicker
              current={responseFormat}
              onChange={onResponseFormatChange}
              disabledFormats={disabledFormats}
            />
            {charCount >= COUNTER_THRESHOLD && (
              <span
                className={cn(
                  'text-small font-mono',
                  charCount >= MAX_CHARS ? 'text-red-600' : 'text-slate-500',
                )}
              >
                {charCount}/{MAX_CHARS}
              </span>
            )}
          </div>
        </div>
        <Button
          onClick={submit}
          disabled={!value.trim() || isSubmitting || quotaExhausted}
          size="lg"
          className="h-10"
          aria-label="Submit question"
        >
          <Send className="h-4 w-4" aria-hidden />
        </Button>
      </div>
    </div>
  );
}

// ─── Format buttons ─────────────────────────────────────────────

const FORMATS: { value: ResponseFormat; icon: typeof Type; label: string }[] = [
  { value: 'text', icon: Type, label: 'Text' },
  { value: 'map', icon: Map, label: 'Map' },
  { value: 'graph', icon: Network, label: 'Graph' },
  { value: 'report', icon: FileText, label: 'Report' },
];

function FormatPicker({
  current,
  onChange,
  disabledFormats,
}: {
  current: ResponseFormat;
  onChange: (f: ResponseFormat) => void;
  disabledFormats: ResponseFormat[];
}) {
  return (
    <div className="flex items-center gap-0.5">
      {FORMATS.map(({ value, icon: Icon, label }) => {
        const disabled = disabledFormats.includes(value);
        const active = current === value && !disabled;
        return (
          <Tooltip key={value}>
            <TooltipTrigger asChild>
              <button
                type="button"
                disabled={disabled}
                onClick={() => onChange(value)}
                className={cn(
                  'flex h-7 w-7 items-center justify-center rounded text-slate-500 transition-colors',
                  active && 'bg-accent text-white hover:bg-accent-700',
                  !active && !disabled && 'hover:bg-slate-100 hover:text-slate-900',
                  disabled && 'cursor-not-allowed opacity-30',
                )}
                aria-pressed={active}
                aria-label={`Response format: ${label}`}
              >
                <Icon className="h-3.5 w-3.5" aria-hidden />
              </button>
            </TooltipTrigger>
            <TooltipContent>
              {disabled ? `${label} (upgrade required)` : label}
            </TooltipContent>
          </Tooltip>
        );
      })}
    </div>
  );
}
